import { vrocManager, vrocFactory, VROCComponentFactory } from '../../_VROC/VROCComponentFactory.js';
import { initHotelAndRoomCards } from '../../_Common/app.js';

export class Hotels {
	constructor() {
		this.activeComponent = null; // Активный компонент (если есть)
		this._debounceTimer = null; // Таймер для debounce
		this.orderId = this._getOrderId(); // Получаем ID заказа один раз
		this._registerComponent();
		this._setupFormSubmitHandler();
		this.setupFilters();
	}

	/**
	 * Инициализирует компонент (фоновая загрузка для attach режима)
	 */
	async init() {
		try {
			console.log('[Hotels] Initializing component in background');
			
			// Создаем компонент в фоне без лоадера
			await this._createComponent();
			
			console.log('[Hotels] Component initialized successfully');
		} catch (error) {
			console.error('[Hotels] Error during initialization:', error);
		}
	}

	/**
	 * Регистрирует базовую конфигурацию компонента в менеджере
	 */
	_registerComponent() {
		const baseConfig = {
			template: 'mod_crm/hotels_content',
			node: '.hotels__content',
			attach: true, // Режим attach - контент уже есть на странице
			moduleId: 3, // Модуль отелей
			params: {
				action: 'loadItems', // Начальный запрос для attach режима
				...(this.orderId && { orderId: this.orderId }) // Добавляем orderId если он есть
			},
			// Дополнительные XPath ветки, которые должны быть включены при рендеринге патчей типа 'add'
			// Это нужно когда шаблон использует данные не только из добавляемого элемента, но и из других веток XML
			additionalXPaths: [
				'/module/dates', // Ветка с датами используется в шаблоне Hotel-card.pug для отображения периода бронирования
				'/module/people',
				'/module/bed_types',
				'/module/room_tags',
			],
			autoActions: {
				updateHotelsList: {
					successMessage: 'Список отелей обновлен',
					errorMessage: 'Ошибка обновления списка отелей'
				}
			},
			validation: {},
			onError: (message, error) => {
				console.error('[Hotels] Component error:', message, error);
			},
			events: VROCComponentFactory.createEvents({
				beforeInit: () => {
					console.log('[Hotels] Component initialization started');
				},
				afterInit: () => {
					console.log('[Hotels] Component initialization completed');
				},
				beforeAction: (data) => {
					console.log('[Hotels] Component action started:', data.action);
				},
				afterAction: (data) => {
					console.log('[Hotels] Component action completed:', data.action);
				},
				error: (data) => {
					console.error('[Hotels] Component error occurred:', data.message);
				}
			}),
			notifications: {
				show: (message, type) => {
					console.log(`[Hotels] ${type.toUpperCase()}: ${message}`);
				}
			}
		};

		vrocManager.registerComponent('hotels', baseConfig);
	}

	/**
	 * Настраивает обработчики событий формы
	 */
	_setupFormSubmitHandler() {
		const form = document.querySelector('.hotels__control');
		if (form) {
			// Обрабатываем все изменения в полях формы, исключая только селект для добавления детей
			const inputs = form.querySelectorAll('input, select, textarea');
			inputs.forEach(input => {
				// Пропускаем только селект для добавления детей
				if (!input.classList.contains('guests-picker__kids-item-select')) {
					// Для текстовых полей также обрабатываем input
					if (input.type === 'text' || input.type === 'number' || input.tagName === 'TEXTAREA') {
						input.addEventListener('input', (event) => {
							// Добавляем debounce для текстовых полей
							clearTimeout(this._debounceTimer);
							this._debounceTimer = setTimeout(() => {
								this._handleFormChange(event);
							}, 500);
						});
					}
					else
					{
						input.addEventListener('change', (event) => {
							this._handleFormChange(event);
						});
					}
				}
			});

			// Настраиваем guests-picker
			this._setupGuestsPicker();

			// Настраиваем datepicker
			this._setupDatePicker();

			// Также обрабатываем сабмит формы
			form.addEventListener('submit', (event) => {
				event.preventDefault();
				this._handleFormChange(event);
			});
		}
	}

	/**
	 * Настраивает guests-picker
	 */
	_setupGuestsPicker() {
		const guestsPicker = document.querySelector('.guests-picker');
		if (!guestsPicker) return;

		// Обработчики для кнопок плюс/минус взрослых
		const adultsMinus = guestsPicker.querySelector('.guests-picker__adults-minus');
		const adultsPlus = guestsPicker.querySelector('.guests-picker__adults-plus');
		const adultsInput = guestsPicker.querySelector('.guests-picker__adults-input');

		if (adultsMinus && adultsPlus && adultsInput) {
			adultsMinus.addEventListener('click', () => {
				const currentValue = parseInt(adultsInput.value) || 0;
				if (currentValue > 0) {
					adultsInput.value = currentValue - 1;
					this._handleFormChange({ target: adultsInput });
				}
			});

			adultsPlus.addEventListener('click', () => {
				const currentValue = parseInt(adultsInput.value) || 0;
				adultsInput.value = currentValue + 1;
				this._handleFormChange({ target: adultsInput });
			});

			adultsInput.addEventListener('change', () => {
				this._handleFormChange({ target: adultsInput });
			});
		}

		// Обработчик кнопки "Готово"
		const readyBtn = guestsPicker.querySelector('.guests-picker__ready');
		if (readyBtn) {
			readyBtn.addEventListener('click', () => {
				this._handleFormChange({ target: readyBtn });
			});
		}
	}

	/**
	 * Настраивает datepicker
	 */
	_setupDatePicker() {
		// Здесь будет логика для обработки выбора даты
		// Пока просто оставим placeholder
		console.log('[Hotels] DatePicker setup - placeholder');
	}

	/**
	 * Обрабатывает изменения в форме фильтров
	 */
	async _handleFormChange(event) {
		try {
			// Создаем компонент если его нет
			if (!this.activeComponent) {
				await this._createComponent();
				return; // При первом создании данные уже загружаются
			}

			console.log('[Hotels] Form changed');

			// Показываем лоадер
			this._showLoader();

			// Делаем прямой AJAX запрос на обновление данных
			await this._updateHotelsData();

		} catch (error) {
			console.error('[Hotels] Error updating hotels list:', error);
		} finally {
			// Скрываем лоадер
			this._hideLoader();
		}
	}

	// ===== ПУБЛИЧНЫЕ МЕТОДЫ =====

	/**
	 * Основной метод обновления данных (для обратной совместимости с внешними вызовами)
	 */
	async updateData() {
		return this.update();
	}


	/**
	 * Публичный метод для обновления компонента (вызывается из app.js)
	 */
	async update() {
		try {
			console.log('[Hotels] Public update called');
			
			// Создаем компонент если его нет
			if (!this.activeComponent) {
				await this._createComponent();
				return; // При первом создании данные уже загружаются
			}

			// Показываем лоадер
			this._showLoader();

			// Обновляем данные
			await this._updateHotelsData();

		} catch (error) {
			console.error('[Hotels] Error in public update:', error);
		} finally {
			// Скрываем лоадер
			this._hideLoader();
		}
	}

	// ===== ПРИВАТНЫЕ МЕТОДЫ =====

	/**
	 * Получает ID заказа со страницы
	 * @returns {string|null} ID заказа
	 */
	_getOrderId() {
		// Вариант 1: Из скрытого поля #orderId
		const orderIdElement = document.querySelector('#orderId');
		if (orderIdElement && orderIdElement.value) return orderIdElement.value;

		// Вариант 2: Из data-атрибута
		const dataElement = document.querySelector('[data-invoice-id]');
		if (dataElement) {
			const idFromData = dataElement.getAttribute('data-invoice-id');
			if (idFromData) return idFromData;
		}

		// Вариант 3: Из URL
		const urlParams = new URLSearchParams(window.location.search);
		const idFromUrl = urlParams.get('id');
		if (idFromUrl) return idFromUrl;

		return null;
	}

	/**
	 * Создает новый компонент
	 */
	async _createComponent() {
		if (this.activeComponent) {
			this.activeComponent = null;
		}

		const component = await vrocManager.createComponent('hotels');
		this.activeComponent = component;

		console.log('[Hotels] Component created successfully');
		return component;
	}

	/**
	 * Обновляет данные отелей через AJAX
	 */
	async _updateHotelsData() {
		try {
			// Используем ServiceManager для получения данных формы
			if (typeof window.serviceManager === 'undefined') {
				console.error('[Hotels] serviceManager is not available');
				throw new Error('ServiceManager not available');
			}

			// Получаем данные через единый метод ServiceManager
			const hotelsData = window.serviceManager.getHotelsFormData();

			// Создаем FormData
			const formData = new FormData();

			// Добавляем все данные в FormData
			Object.entries(hotelsData).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach(item => {
						formData.append(`${key}[]`, item);
					});
				} else {
					formData.append(key, value);
				}
			});

			// Добавляем action
			formData.set('action', 'loadItems');

			// Добавляем orderId если он есть
			if (this.orderId) {
				formData.set('orderId', this.orderId);
				console.log('[Hotels] Adding orderId to request:', this.orderId);
			}

			console.log('[Hotels] Sending AJAX request to update hotels with data:', hotelsData);

			const response = await fetch('/ajax.php?mid=3', {
				method: 'POST',
				body: formData
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const xmlText = await response.text();
			console.log('[Hotels] Received XML response, updating component');

			// Обновляем компонент с новыми данными
			await this.activeComponent.vroc.updateComponent(this.activeComponent.id, xmlText);

			console.log('[Hotels] Hotels list updated successfully');

			// Ждем следующий frame для гарантии рендеринга DOM после VROC обновления
			// VROC.applyPatch вызывается без await, поэтому нужно дождаться рендеринга
			requestAnimationFrame(() => {
				// Инициализируем события для карточек отелей и номеров после обновления
				initHotelAndRoomCards();
				console.log('[Hotels] Hotel and room cards events initialized');
			});

		} catch (error) {
			console.error('[Hotels] Error in _updateHotelsData:', error);
			throw error;
		}
	}

	/**
	 * Показывает лоадер
	 */
	_showLoader() {
		const loader = document.querySelector('.hotels__loader');
		if (loader) {
			loader.classList.add('active');
		}
	}

	/**
	 * Скрывает лоадер
	 */
	_hideLoader() {
		const loader = document.querySelector('.hotels__loader');
		if (loader) {
			loader.classList.remove('active');
		}
	}

	// ===== МЕТОДЫ ДЛЯ УПРАВЛЕНИЯ КОМПОНЕНТОМ =====

	/**
	 * Уничтожает активный компонент
	 */
	destroyActiveComponent() {
		if (this.activeComponent) {
			vrocManager.destroyComponent(this.activeComponent.id);
			this.activeComponent = null;
			console.log('[Hotels] Active component destroyed');
		}
	}

	/**
	 * Уничтожает все компоненты
	 */
	destroyAllComponents() {
		vrocManager.destroyComponentsByName('hotels');
		this.activeComponent = null;
		console.log('[Hotels] All components destroyed');
	}

	/**
	 * Получает активный компонент
	 */
	getActiveComponent() {
		return this.activeComponent;
	}

	// ===== МЕТОДЫ УПРАВЛЕНИЯ ТЕГАМИ ФИЛЬТРОВ =====

	/**
	 * Инициализирует обработчики для фильтров
	 */
	setupFilters() {
		// Находим попап с фильтрами
		const filtersPopup = document.querySelector('.popup[data-popup-name="hotels-filters"]');
		if (!filtersPopup) return;

		// Добавляем обработчики на чекбоксы фильтров
		const filterCheckboxes = filtersPopup.querySelectorAll('input[type="checkbox"]');
		filterCheckboxes.forEach(checkbox => {
			checkbox.addEventListener('change', (e) => {
				this._handleFilterChange(e.target);
			});
		});

		// Настраиваем кнопку "Сбросить всё"
		const resetAllBtn = document.querySelector('.hotels-filters-controls__reset');
		if (resetAllBtn) {
			resetAllBtn.addEventListener('click', () => {
				this.resetFilters();
			});
		}

		console.log('[Hotels] Filters setup completed');
	}

	/**
	 * Обрабатывает изменение фильтра (добавление/удаление тега)
	 */
	_handleFilterChange(checkbox) {
		if (checkbox.checked) {
			this._addFilterTag(checkbox);
		} else {
			this._removeFilterTag(checkbox);
		}

		// Обновляем видимость кнопки "Сбросить всё"
		this._updateResetAllButton();
	}

	/**
	 * Добавляет тег активного фильтра
	 */
	_addFilterTag(checkbox) {
		const template = document.getElementById('filter-tag-template');
		if (!template) {
			console.error('[Hotels] Filter tag template not found');
			return;
		}

		const tagsList = document.querySelector('.hotels-filters-controls__choiced-list');
		if (!tagsList) {
			console.error('[Hotels] Filter tags list not found');
			return;
		}

		// Клонируем шаблон
		const clone = template.content.cloneNode(true);
		const li = clone.querySelector('li');
		const btn = clone.querySelector('button');
		const caption = clone.querySelector('.hotels-filters-controls__choiced-caption');

		// Получаем текст фильтра из label
		const label = document.querySelector(`label[for="${checkbox.id}"]`);
		let filterText = checkbox.value;

		if (label) {
			// Извлекаем только текст названия фильтра, без счётчика
			const titleElement = label.querySelector('.service-filters__item-title');
			if (titleElement) {
				// Берем только текстовое содержимое, исключая вложенные элементы
				filterText = titleElement.childNodes[0]?.textContent?.trim() || checkbox.value;
			}
		}

		caption.textContent = filterText;

		// Сохраняем ID чекбокса в data-атрибуте
		li.setAttribute('data-filter-id', checkbox.id);

		// Добавляем обработчик на кнопку удаления
		btn.addEventListener('click', () => {
			this._removeFilterTagByElement(li, checkbox);
		});

		// Добавляем в список
		tagsList.appendChild(clone);

		console.log('[Hotels] Filter tag added:', filterText);
	}

	/**
	 * Удаляет тег фильтра
	 */
	_removeFilterTag(checkbox) {
		const tag = document.querySelector(`li[data-filter-id="${checkbox.id}"]`);
		if (tag) {
			tag.remove();
			console.log('[Hotels] Filter tag removed');
		}
	}

	/**
	 * Удаляет тег фильтра по элементу и снимает чекбокс
	 */
	async _removeFilterTagByElement(tagElement, checkbox) {
		// Снимаем чекбокс
		checkbox.checked = false;

		// Удаляем тег
		tagElement.remove();

		// Обновляем видимость кнопки "Сбросить всё"
		this._updateResetAllButton();

		// Обновляем список отелей
		await this._handleFormChange({ target: checkbox });

		console.log('[Hotels] Filter removed and hotels list updated');
	}

	/**
	 * Обновляет видимость кнопки "Сбросить всё"
	 */
	_updateResetAllButton() {
		const resetAllBtn = document.querySelector('.hotels-filters-controls__reset');
		if (!resetAllBtn) return;

		const tagsList = document.querySelector('.hotels-filters-controls__choiced-list');
		const hasTags = tagsList && tagsList.children.length > 0;

		resetAllBtn.style.display = hasTags ? 'flex' : 'none';
	}

	/**
	 * Очищает все теги фильтров
	 */
	_clearAllFilterTags() {
		const tagsList = document.querySelector('.hotels-filters-controls__choiced-list');
		if (tagsList) {
			tagsList.innerHTML = '';
		}

		// Обновляем видимость кнопки "Сбросить всё"
		this._updateResetAllButton();
	}

	/**
	 * Синхронизирует теги с состоянием чекбоксов (вызывается при инициализации)
	 */
	syncFilterTags() {
		// Очищаем все теги
		this._clearAllFilterTags();

		// Находим все отмеченные чекбоксы и создаем для них теги
		const filtersPopup = document.querySelector('.popup[data-popup-name="hotels-filters"]');
		if (filtersPopup) {
			const checkedFilters = filtersPopup.querySelectorAll('input[type="checkbox"]:checked');
			checkedFilters.forEach(checkbox => {
				this._addFilterTag(checkbox);
			});
		}

		// Обновляем видимость кнопки "Сбросить всё"
		this._updateResetAllButton();

		console.log('[Hotels] Filter tags synced');
	}

	/**
	 * Применяет фильтры и закрывает попап
	 */
	applyFilters() {
		// Закрываем попап через PopupManager
		if (typeof window.popupManager !== 'undefined') {
			window.popupManager.close('hotels-filters');
		}
		console.log('[Hotels] Filters applied');
	}

	/**
	 * Сбрасывает все фильтры и закрывает попап
	 */
	async resetFilters() {
		// Находим попап с фильтрами
		const filtersPopup = document.querySelector('.popup[data-popup-name="hotels-filters"]');

		if (filtersPopup) {
			// Сбрасываем все чекбоксы
			const checkboxes = filtersPopup.querySelectorAll('input[type="checkbox"]');
			checkboxes.forEach(checkbox => {
				checkbox.checked = false;
			});

			// Сбрасываем все текстовые поля, если есть
			const inputs = filtersPopup.querySelectorAll('input[type="text"], input[type="number"]');
			inputs.forEach(input => {
				input.value = '';
			});

			// Сбрасываем все селекты, если есть
			const selects = filtersPopup.querySelectorAll('select');
			selects.forEach(select => {
				select.selectedIndex = 0;
			});

			console.log('[Hotels] Filters reset');
		}

		// Очищаем все теги фильтров
		this._clearAllFilterTags();

		// Закрываем попап через PopupManager
		if (typeof window.popupManager !== 'undefined') {
			window.popupManager.close('hotels-filters');
		}

		// Обновляем список отелей с учетом сброшенных фильтров
		await this.update();
	}
}

// Глобальная регистрация убрана - теперь только через app.js
